import { Search } from "lucide-react";
import { useState } from "react";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgCheckbox } from "@intelligems/ig-theme/IgCheckbox";
import { IgLoading } from "@intelligems/ig-theme/IgLoading";
import { IgRadio } from "@intelligems/ig-theme/IgRadio";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Box, Stack, useTheme } from "@mui/material";
import {
  ShopifyItemKind,
  useShopifyPickerItems,
} from "@src/components/MainApp/ShopifyItemPicker/useShopifyPickerItems";
import {
  formatCatalogLabel,
  SHOPIFY_ENTITY_COPY,
  ShopifyEntityPick,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/shopifyEntityPickerCopy";
import { surveyCheckboxSx } from "@src/features/ai/components/WorkflowEditor/execution/panel/surveyCheckboxSx";
import { IgCoreTextColors } from "@intelligems/ig-design-tokens/constants";

// A store with thousands of tags must never render thousands of rows; search
// narrows the rest, and the footer says so.
const MAX_RENDERED_ITEMS = 250;

/**
 * Figma's menu height — about five and a half rows, so a longer list reads as
 * scrollable. A cap, not a size: a short list hugs its rows (ENG-7894).
 */
const MENU_MAX_HEIGHT = 182;

/** Rows (8px padding around an 18px line) that fit under the cap whole. */
const ROWS_UNDER_CAP = Math.floor(MENU_MAX_HEIGHT / 34);

interface ShopifyEntityComboboxProps {
  entity: ShopifyItemKind;
  multiSelect: boolean;
  disabled: boolean;
  /**
   * The running selection. Owned by the card, which counts it in its header
   * and re-seeds it from the recorded answer when the stepper comes back.
   */
  selected: ShopifyEntityPick[];
  onSelectedChange: (selected: ShopifyEntityPick[]) => void;
  onConfirm: (picked: ShopifyEntityPick[]) => void;
  onNone: () => void;
}

/**
 * The Figma "Atlas / Chat combobox" (516:3185): the body a `shopify-pick`
 * question renders inside the survey card for a flat catalog kind
 * (collections, tags, vendors, product types). The whole list lives in the
 * card, which docks in the composer's place while it is open — a scrollable
 * run of rows, then a footer holding the search field and the explicit Confirm
 * selections. No dialog: the card IS the picker, and the card's own X is the
 * way out.
 *
 * Layout is the spec verbatim (theme spacing unit is 4px): the menu inset 8px
 * with a 6px scrollbar 4px beside it, 32px rows padded 8px on a 2px radius, a
 * 40px fade over the last row, and a footer padded 12px at the sides, 8px above
 * and 12px below, holding a 40px search field that fills the row and a 40px
 * Confirm only as wide as its label. Search filters the loaded list
 * client-side, which is what the store-wide `useShopifyPickerItems` lists make
 * possible. Catalog values are shown formatted (`formatCatalogLabel`) — the
 * raw value is still the id the agent receives.
 *
 * The row control follows the question: a `multiSelect` question assembles a
 * set from checkbox rows, while a single-answer question uses radio rows —
 * picking a second row replaces the first, which is what a checkbox row would
 * silently do while promising the opposite.
 */
export const ShopifyEntityCombobox = ({
  entity,
  multiSelect,
  disabled,
  selected,
  onSelectedChange,
  onConfirm,
  onNone,
}: ShopifyEntityComboboxProps) => {
  const theme = useTheme();
  const copy = SHOPIFY_ENTITY_COPY[entity];
  const { items: rawItems, loading } = useShopifyPickerItems(entity);
  const items = rawItems.map((item) => ({
    id: item.id,
    label: formatCatalogLabel(entity, item.label),
  }));

  const [search, setSearch] = useState("");

  const selectedIds = new Set(selected.map((item) => item.id));
  const normalizedSearch = search.trim().toLowerCase();
  const filtered = normalizedSearch
    ? items.filter((item) =>
        item.label.toLowerCase().includes(normalizedSearch)
      )
    : items;
  const rendered = filtered.slice(0, MAX_RENDERED_ITEMS);

  // Checkboxes assemble a set; a single-answer question offers radios, whose
  // pick always replaces the running one.
  const RowControl = multiSelect ? IgCheckbox : IgRadio;

  const toggleItem = (item: ShopifyEntityPick) => {
    if (selected.some((entry) => entry.id === item.id)) {
      onSelectedChange(selected.filter((entry) => entry.id !== item.id));
      return;
    }
    onSelectedChange(multiSelect ? [...selected, item] : [item]);
  };

  let menu;
  if (loading && items.length === 0) {
    menu = (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
        <IgLoading />
      </Stack>
    );
  } else if (filtered.length === 0) {
    menu = (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
        <IgTypography variant="body2" sx={{ color: "text.secondary" }}>
          No {copy.plural} found
        </IgTypography>
      </Stack>
    );
  } else {
    menu = (
      <Stack
        data-testid="shopify-entity-combobox-list"
        sx={{
          minHeight: 0,
          minWidth: 0,
          overflowY: "auto",
          // Figma's in-card scrollbar (516:3199): 6px wide, 4px radius, a
          // #f1f1f1 thumb sized to the list — not the app's global 0.4em one.
          scrollbarColor: `${theme.igIconColors.darkHover} transparent`,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.igIconColors.darkHover,
            borderRadius: theme.spacing(1),
          },
        }}
      >
        {rendered.map((item) => (
          <Stack
            key={item.id}
            component="label"
            direction="row"
            alignItems="center"
            gap={2}
            sx={{
              borderRadius: "2px",
              cursor: disabled ? "default" : "pointer",
              flexShrink: 0,
              minHeight: 32,
              p: 2,
              "&:hover": {
                backgroundColor: disabled
                  ? "transparent"
                  : theme.igBackgroundColors.interactive.neutral.hover,
              },
            }}
          >
            <RowControl
              checked={selectedIds.has(item.id)}
              disabled={disabled}
              name={multiSelect ? undefined : `shopify-pick-${entity}`}
              onChange={() => toggleItem(item)}
              size="small"
              sx={surveyCheckboxSx}
            />
            <IgTypography
              variant="body2"
              noWrap
              sx={{
                color: IgCoreTextColors.primary,
                lineHeight: "18px",
                minWidth: 0,
              }}
            >
              {item.label}
            </IgTypography>
          </Stack>
        ))}
        {filtered.length > rendered.length && (
          <IgTypography
            variant="body3"
            sx={{ color: "text.secondary", px: 2, py: 1.5 }}
          >
            Showing the first {MAX_RENDERED_ITEMS} of {filtered.length} matches
            — refine your search to see the rest.
          </IgTypography>
        )}
      </Stack>
    );
  }

  return (
    <>
      <Stack
        data-testid="shopify-entity-combobox"
        sx={{ maxHeight: MENU_MAX_HEIGHT, px: 2 }}
      >
        {menu}
      </Stack>
      <Stack
        direction="row"
        gap={2}
        sx={{ position: "relative", px: 3, pt: 2, pb: 3 }}
      >
        {/* Figma's scroll fade: the last row dissolves into the footer rather
            than being clipped mid-glyph, inset 1.5px so it clears the card's
            hairline, 28px above the footer. Only a list that scrolls has a
            clipped row to fade; a short one ends on its last whole row. */}
        {rendered.length > ROWS_UNDER_CAP ? (
          <Box
            aria-hidden
            data-testid="shopify-entity-combobox-fade"
            sx={{
              background: `linear-gradient(to bottom, transparent, ${theme.igBackgroundColors.baseLightHigh})`,
              height: 40,
              left: "1.5px",
              pointerEvents: "none",
              position: "absolute",
              right: "1.5px",
              top: -28,
            }}
          />
        ) : null}
        <IgTextField
          fullWidth
          disabled={disabled}
          placeholder={copy.searchPlaceholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <Box
                  sx={{ display: "flex", mr: 1 }}
                  aria-hidden
                  data-testid="shopify-entity-combobox-search-icon"
                >
                  <Search size={16} color={theme.igIconColors.light} />
                </Box>
              ),
            },
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiInputBase-root": { height: 40, px: 3 },
            "& .MuiInputBase-input": { px: 0 },
          }}
        />
        {multiSelect && (
          <IgButton
            variant="outlined"
            disabled={disabled}
            onClick={onNone}
            sx={{ flexShrink: 0, height: 40 }}
          >
            None
          </IgButton>
        )}
        <IgButton
          variant="outlined"
          disabled={disabled || (!multiSelect && selected.length === 0)}
          onClick={() => onConfirm(selected)}
          sx={{
            flexShrink: 0,
            height: 40,
            lineHeight: "21px",
            px: 4,
            py: 0,
            "&.Mui-disabled": {
              borderColor: theme.igBorders.core.default,
              color: IgCoreTextColors.placeholder,
            },
          }}
        >
          Confirm selections
        </IgButton>
      </Stack>
    </>
  );
};
